import { styled } from '@linaria/react';
import { Chip, NumberInput, Select, TextInput } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { Text } from '../../../../ui-library/typography/typography';
import {
  DAY_LABELS_SHORT,
  Frequency,
  FREQUENCY_OPTIONS,
  type FrequencyState,
  HOUR_OPTIONS,
  humanizeSchedule,
  isValidHourCron,
  TIMEZONE_OPTIONS,
} from '../shared/schedule.helpers';
import { inputStyles } from '../shared/input-styles';
import { ScheduleType } from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import { computeEffectiveCron, hydrateScheduleState, isScheduleStateValid } from './agent-schedule-builder.helpers';

type AgentScheduleBuilderProps = {
  value: string | null;
  onChange: (cronExpression: string | null) => void;
  onValidityChange?: (isValid: boolean) => void;
  fieldIdPrefix: string;
};

export function AgentScheduleBuilder({ value, onChange, onValidityChange, fieldIdPrefix }: AgentScheduleBuilderProps) {
  const initial = useMemo(() => hydrateScheduleState(value), [value]);
  const [frequencyState, setFrequencyState] = useState<FrequencyState>(initial.frequencyState);
  const [isAdvanced, setIsAdvanced] = useState<boolean>(initial.isAdvanced);
  const [customCron, setCustomCron] = useState<string>(initial.customCron);
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    const next = hydrateScheduleState(value);
    setFrequencyState(next.frequencyState);
    setIsAdvanced(next.isAdvanced);
    setCustomCron(next.customCron);
    setHasHydrated(true);
    // Re-hydrate whenever the consumer swaps in a different persisted value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const effectiveCron = useMemo(
    () => computeEffectiveCron(frequencyState, isAdvanced, customCron),
    [frequencyState, isAdvanced, customCron],
  );

  const advancedCronError = useMemo<string | null>(() => {
    if (!isAdvanced || !customCron) return null;
    if (!isValidHourCron(customCron)) {
      return 'Must be a 5-field cron expression with minute = 0';
    }
    return null;
  }, [isAdvanced, customCron]);

  const schedulePreviewLabel = useMemo<string | null>(() => {
    if (!effectiveCron) return null;
    return humanizeSchedule(ScheduleType.CRON, effectiveCron, frequencyState.timezone);
  }, [effectiveCron, frequencyState.timezone]);

  useEffect(() => {
    if (!hasHydrated) return;
    onChange(effectiveCron);
    onValidityChange?.(isScheduleStateValid(effectiveCron, isAdvanced));
  }, [hasHydrated, effectiveCron, isAdvanced, onChange, onValidityChange]);

  return (
    <Wrapper>
      {!isAdvanced && (
        <BuilderStack>
          <BuilderRow>
            <FieldLabel htmlFor={`${fieldIdPrefix}-repeat`}>Repeat</FieldLabel>
            <Select
              id={`${fieldIdPrefix}-repeat`}
              value={frequencyState.frequency}
              onChange={(next) => next && setFrequencyState((prev) => ({ ...prev, frequency: next as Frequency }))}
              data={FREQUENCY_OPTIONS}
              allowDeselect={false}
              styles={inputStyles}
            />
          </BuilderRow>

          {frequencyState.frequency === Frequency.WEEKLY && (
            <BuilderRow role="group" aria-labelledby={`${fieldIdPrefix}-days-label`}>
              <FieldLabel id={`${fieldIdPrefix}-days-label`}>Days</FieldLabel>
              <Chip.Group
                multiple
                value={frequencyState.daysOfWeek.map(String)}
                onChange={(values) =>
                  setFrequencyState((prev) => ({
                    ...prev,
                    daysOfWeek: (values as string[]).map(Number).sort((a, b) => a - b),
                  }))
                }
              >
                <DayChips>
                  {DAY_LABELS_SHORT.map((label, index) => (
                    <Chip
                      key={label}
                      value={String(index)}
                      size="sm"
                      styles={{ label: { paddingLeft: 10, paddingRight: 10 }, iconWrapper: { display: 'none' } }}
                      classNames={{ label: 'agent-day-chip' }}
                    >
                      {label}
                    </Chip>
                  ))}
                </DayChips>
              </Chip.Group>
            </BuilderRow>
          )}

          {frequencyState.frequency === Frequency.MONTHLY && (
            <BuilderRow>
              <FieldLabel htmlFor={`${fieldIdPrefix}-day-of-month`}>Day of month</FieldLabel>
              <NumberInput
                id={`${fieldIdPrefix}-day-of-month`}
                value={frequencyState.dayOfMonth}
                onChange={(next) => {
                  const parsed = typeof next === 'number' ? next : Number(next);
                  if (!Number.isFinite(parsed)) return;
                  const clamped = Math.min(28, Math.max(1, Math.round(parsed)));
                  setFrequencyState((prev) => ({ ...prev, dayOfMonth: clamped }));
                }}
                min={1}
                max={28}
                step={1}
                clampBehavior="strict"
                allowDecimal={false}
                allowNegative={false}
                styles={inputStyles}
              />
            </BuilderRow>
          )}

          <TimeAndZoneRow>
            <BuilderRow>
              <FieldLabel htmlFor={`${fieldIdPrefix}-time`}>Time</FieldLabel>
              <Select
                id={`${fieldIdPrefix}-time`}
                value={String(frequencyState.hour)}
                onChange={(next) => next && setFrequencyState((prev) => ({ ...prev, hour: Number(next) }))}
                data={HOUR_OPTIONS}
                allowDeselect={false}
                styles={inputStyles}
              />
            </BuilderRow>

            <BuilderRow>
              <FieldLabel htmlFor={`${fieldIdPrefix}-timezone`}>Timezone</FieldLabel>
              <Select
                id={`${fieldIdPrefix}-timezone`}
                value={frequencyState.timezone}
                onChange={(next) => next && setFrequencyState((prev) => ({ ...prev, timezone: next }))}
                data={TIMEZONE_OPTIONS}
                searchable
                allowDeselect={false}
                styles={inputStyles}
              />
            </BuilderRow>
          </TimeAndZoneRow>

          <Text size="tiny" color="var(--ink-lighter)">
            Schedules run on the hour. Times shift by one hour after daylight-saving transitions.
          </Text>
        </BuilderStack>
      )}

      {isAdvanced && (
        <BuilderStack>
          <BuilderRow>
            <FieldLabel htmlFor={`${fieldIdPrefix}-cron-expression`}>Cron expression</FieldLabel>
            <TextInput
              id={`${fieldIdPrefix}-cron-expression`}
              placeholder="Minute Hour Day-of-month Month Day-of-week (e.g. 0 9 * * 1)"
              value={customCron}
              onChange={(event) => setCustomCron(event.currentTarget.value)}
              styles={inputStyles}
              error={advancedCronError}
            />
          </BuilderRow>
          <Text size="tiny" color="var(--ink-lighter)">
            Cron is evaluated in UTC. Minute field must be 0 — sub-hour schedules are not supported.
          </Text>
        </BuilderStack>
      )}

      <AdvancedToggleButton type="button" onClick={() => setIsAdvanced((prev) => !prev)}>
        {isAdvanced ? 'Use schedule builder' : 'Advanced (custom cron)'}
      </AdvancedToggleButton>

      {schedulePreviewLabel && (
        <SchedulePreview>
          <Text size="small" weight="bold" color="var(--ink-darkest)">
            {schedulePreviewLabel}
          </Text>
          {!isAdvanced && effectiveCron && (
            <Text size="tiny" color="var(--ink-lighter)">
              Sends as {effectiveCron} (UTC)
            </Text>
          )}
        </SchedulePreview>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BuilderStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
`;

const BuilderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-dark);
`;

const TimeAndZoneRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: 12px;
`;

const DayChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  .agent-day-chip[data-checked] {
    background: var(--secondary-base);
    color: var(--base-white);
    border-color: var(--secondary-base);
  }
`;

const AdvancedToggleButton = styled.button`
  align-self: flex-start;
  background: transparent;
  border: none;
  padding: 4px 0;
  color: var(--secondary-base);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SchedulePreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--sky-lighter);
  border: 1px solid var(--sky-light);
`;

export type { AgentScheduleBuilderProps };
